import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  PLANNED_STATUS_LABELS,
  fetchImports,
  importFile,
  plDay,
  type FuelSurchargeImport,
  type ImportResult,
  type PlannedStatus,
} from './fuelSurchargesApi';

/**
 * Wgranie pliku „klient → korekta” od CS (Odpowiedzi CS 20.10.2026, pkt 4).
 * Najpierw podgląd: każdy wiersz z oceną (nowe przypisanie, zmiana, bez
 * zmian, błąd z powodem) i raport nieznanych kontrahentów i rodzajów.
 * Zapis dopiero po potwierdzeniu człowieka, z powodem — tego samego planu,
 * który widać w podglądzie (inny plan serwer odrzuca).
 */

export const FORMAT_NOTE =
  'Plik CSV albo Excel (pierwszy arkusz). Pierwszy wiersz to nagłówki: identyfikator kontrahenta w TMS („ID TMS”) albo NIP, rodzaj korekty („Korekta” — nazwa albo kod z kartoteki) i data początku („Od”); opcjonalnie „Do”, „Kontrahent” (tylko do raportu) i „Uwagi”. Daty: 2026-11-01 albo 01.11.2026. Kontrahenta dopasowujemy po identyfikatorze z TMS albo NIP — nigdy po nazwie.';

const STATUS_COLORS: Record<PlannedStatus, 'success' | 'info' | 'default' | 'error'> = {
  new: 'success',
  change: 'info',
  unchanged: 'default',
  error: 'error',
};

export const ImportTab = ({ canEdit, onApplied }: { canEdit: boolean; onApplied: () => void }) => {
  const notify = useNotify();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState<FuelSurchargeImport[]>([]);

  const loadImports = useCallback(async () => {
    try {
      setImports(await fetchImports());
    } catch {
      setImports([]);
    }
  }, []);

  useEffect(() => {
    void loadImports();
  }, [loadImports]);

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      setPreview(await importFile(file, { dryRun: true }));
      setReason(`Plik od CS: ${file.name}`);
    } catch (e) {
      setPreview(null);
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toApply = preview ? preview.summary.new + preview.summary.change : 0;

  const confirm = async () => {
    if (!file || !preview) return;
    setBusy(true);
    try {
      const done = await importFile(file, { dryRun: false, planHash: preview.planHash, reason: reason.trim() });
      notify(`Zapisano przypisań: ${done.applied ?? 0}`, { type: 'success' });
      setPreview(null);
      setFile(null);
      void loadImports();
      onApplied();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="fuel-import">
      <Alert severity="info" sx={{ mb: 2 }}>
        {FORMAT_NOTE}
      </Alert>
      {canEdit ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          <Button variant="outlined" component="label">
            Wybierz plik
            <input
              hidden
              type="file"
              accept=".csv,.txt,.xlsx,.xlsm"
              data-testid="fuel-import-file"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setPreview(null);
              }}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : 'nie wybrano pliku'}</Typography>
          <Button variant="contained" disabled={!file || busy} onClick={() => void runPreview()}>
            Podgląd
          </Button>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Plik od CS wgrywa przyjęcie zleceń.
        </Typography>
      )}

      {preview && (
        <Box data-testid="fuel-import-preview" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
            {(['new', 'change', 'unchanged', 'error'] as const).map((s) => (
              <Chip
                key={s}
                size="small"
                color={STATUS_COLORS[s]}
                variant={s === 'unchanged' ? 'outlined' : 'filled'}
                label={`${PLANNED_STATUS_LABELS[s]}: ${preview.summary[s]}`}
                data-testid={`fuel-import-summary-${s}`}
              />
            ))}
          </Stack>
          {(preview.unknownContractors.length > 0 || preview.unknownTypes.length > 0) && (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="fuel-import-unknown">
              {preview.unknownContractors.length > 0 && (
                <div>Nieznani kontrahenci: {preview.unknownContractors.join(', ')}</div>
              )}
              {preview.unknownTypes.length > 0 && <div>Nieznane rodzaje korekt: {preview.unknownTypes.join(', ')}</div>}
            </Alert>
          )}
          <Table size="small" data-testid="fuel-import-rows">
            <TableHead>
              <TableRow>
                <TableCell>Wiersz</TableCell>
                <TableCell>Kontrahent</TableCell>
                <TableCell>Korekta</TableCell>
                <TableCell>Od</TableCell>
                <TableCell>Ocena</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.map((r) => (
                <TableRow key={r.rowNo} data-testid="fuel-import-row" data-status={r.status}>
                  <TableCell>{r.rowNo}</TableCell>
                  <TableCell>
                    {r.contractor?.legalName ?? r.file.name ?? '—'}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {[r.file.tmsId ? `TMS ${r.file.tmsId}` : null, r.file.taxId ? `NIP ${r.file.taxId}` : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.type?.name ?? r.file.type ?? '—'}</TableCell>
                  <TableCell>
                    {r.validFrom ? plDay(r.validFrom) : (r.file.from ?? '—')}
                    {r.validTo ? ` – ${plDay(r.validTo)}` : ''}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLORS[r.status]} label={PLANNED_STATUS_LABELS[r.status]} />
                    {r.endsPrevious ? (
                      <Typography variant="caption" component="div">
                        poprzednia „{r.endsPrevious.typeName}” kończy się {plDay(r.endsPrevious.validTo)}
                      </Typography>
                    ) : null}
                    {r.problems.map((p) => (
                      <Typography key={p} variant="caption" color="error" component="div">
                        {p}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', mt: 2 }}>
            <TextField
              label="Powód"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'fuel-import-reason' } }}
            />
            <Button
              variant="contained"
              disabled={toApply === 0 || reason.trim() === '' || busy}
              onClick={() => void confirm()}
            >
              Zapisz przypisania ({toApply})
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Zapisują się tylko „nowe przypisanie” i „zmiana korekty”; wiersze z błędem zostają w raporcie wgrania.
          </Typography>
        </Box>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Wgrane pliki
      </Typography>
      {imports.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Jeszcze żaden.
        </Typography>
      ) : (
        <Table size="small" data-testid="fuel-imports">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Kto</TableCell>
              <TableCell>Plik</TableCell>
              <TableCell align="right">Wierszy</TableCell>
              <TableCell align="right">Zapisane</TableCell>
              <TableCell align="right">Bez zmian</TableCell>
              <TableCell align="right">Błędy</TableCell>
              <TableCell>Powód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {imports.map((i) => (
              <TableRow key={i.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(i.createdAt)}</TableCell>
                <TableCell>{i.createdByName ?? '—'}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{i.fileName}</TableCell>
                <TableCell align="right">{i.rowsTotal}</TableCell>
                <TableCell align="right">{i.applied}</TableCell>
                <TableCell align="right">{i.unchanged}</TableCell>
                <TableCell align="right">{i.errors}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{i.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
